import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, Phone, ArrowDown } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { CONTACT } from "../i18n";
import { RevealLines } from "./Reveal";

const HERO_BG =
  "https://images.unsplash.com/photo-1764605206511-7a649d9df63b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBibGFjayUyMHNlZGFuJTIwbmlnaHQlMjBkcml2aW5nfGVufDB8fHx8MTc4NDgzNDUwOXww&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative h-[100svh] w-full overflow-hidden" data-testid="hero-section">
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={HERO_BG}
          alt="Luxury black sedan at night in Kyrenia"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/65" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />
        <div className="absolute inset-0 gold-glow" />
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-center"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 1 }}
          className="font-mono text-xs tracking-[0.3em] text-[#d4af37] mb-6 md:mb-8"
          data-testid="hero-overline"
        >
          {t.hero.overline}
        </motion.p>

        <h1 className="font-serif text-white leading-[0.9] text-6xl sm:text-7xl lg:text-8xl xl:text-9xl tracking-tighter">
          <RevealLines lines={t.hero.titleLines} delay={0.35} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="font-serif italic text-2xl md:text-3xl text-[#f7e7ce] mt-6 md:mt-8"
          data-testid="hero-tagline"
        >
          {t.hero.tagline}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 1 }}
          className="font-sans text-base md:text-lg text-white/70 max-w-xl mt-6 leading-relaxed"
        >
          {t.hero.desc}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="flex flex-wrap items-center gap-4 mt-10"
        >
          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-pill inline-flex items-center gap-3 rounded-full bg-[#25D366] px-7 py-4 font-sans font-medium text-black hover:scale-[1.03]"
            data-testid="hero-whatsapp-btn"
          >
            <MessageCircle size={20} />
            {t.hero.cta}
          </a>
          <a
            href={`tel:${CONTACT.phoneTel}`}
            className="btn-pill inline-flex items-center gap-3 rounded-full border border-white/25 px-7 py-4 font-sans font-medium text-white hover:bg-white hover:text-black"
            data-testid="hero-call-btn"
          >
            <Phone size={20} />
            {t.hero.call}
          </a>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] text-white/40 uppercase">
          {t.hero.scroll}
        </span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} className="text-[#d4af37]" />
        </motion.div>
      </motion.div>
    </section>
  );
};
